import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { faChartSimple, faList } from '@fortawesome/pro-duotone-svg-icons';
import { TooltipEscapeDirective } from '../../directives/tooltip-escape.directive';

/**
 * The chart ↔ table switch a host mounts beside its chart — for a host that owns the flip itself (its chart sits in
 * a box that scrolls, so the chart's own corner toggle would ride the scroll) and binds the chart's `[(showTable)]`
 * to the same value.
 *
 * Two icons side by side, the chart and the list, with the view in use lit. Both views are on screen, so the control
 * says it is a choice of two, and it keeps its place in both views. A single icon that showed the OTHER view (a faint
 * four-square grid for "table", the glyph another page uses for its cards) had told a member nothing: "I have no idea
 * what it means, being normal user".
 *
 * Each button names itself under the pointer and under the keyboard (the tooltip's default reaches a pointer alone;
 * it opens to the LEFT, toward the header's free room — under a switch at a panel's edge it was squeezed to two
 * lines; the right-hand button's name therefore lies over its neighbour while it shows), carries the same words as
 * its accessible name, and says which view is in use through `aria-pressed`. The group takes its name from the
 * heading the host points it at. An Escape that puts a keyboard-shown name away is that name's alone
 * (`TooltipEscapeDirective`): the panel around the switch stays open on it.
 */
@Component({
  selector: 'app-chart-view-switch',
  standalone: true,
  imports: [FontAwesomeModule, TranslateModule, TooltipModule, TooltipEscapeDirective],
  template: `
    <div class="view-switch" role="group" [attr.aria-labelledby]="labelledBy()">
      <button type="button" class="view-btn" [class.active]="!table()" [attr.aria-pressed]="!table()"
        [attr.aria-label]="'charts.showChart' | translate" [pTooltip]="'charts.showChart' | translate"
        tooltipPosition="left" tooltipEvent="both" (click)="show(false)">
        <fa-duotone-icon [icon]="faChartSimple" />
      </button>
      <button type="button" class="view-btn" [class.active]="table()" [attr.aria-pressed]="table()"
        [attr.aria-label]="'charts.showTable' | translate" [pTooltip]="'charts.showTable' | translate"
        tooltipPosition="left" tooltipEvent="both" (click)="show(true)">
        <fa-duotone-icon [icon]="faList" />
      </button>
    </div>
  `,
  styles: [`
    :host { display: inline-flex; }
    /* Full strength for both layers: the duotone set paints part of a glyph (a list's three lines, a chart's middle
       bar) in the layer it shows at 40%, and a small control's icon drawn that way reads as faded, or as switched off. */
    .view-switch { display: inline-flex; gap: 2px; --fa-secondary-opacity: 1; }
    .view-btn {
      display: inline-flex; align-items: center; justify-content: center;
      width: 1.75rem; height: 1.75rem; padding: 0;
      background: transparent; border: none; border-radius: 4px; cursor: pointer;
      color: var(--p-text-secondary-color); font-size: 0.8125rem;
      transition: background 0.15s, color 0.15s;
    }
    .view-btn:hover, .view-btn:focus-visible { background: var(--p-surface-hover); color: var(--p-text-color); }
    /* The view in use: a tile under its icon, the icon in the text colour. */
    .view-btn.active { background: var(--p-surface-200); color: var(--p-text-color); }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartViewSwitchComponent {
  /** Whether the table is shown; the host binds it one way or both. */
  readonly table = model(false);
  /** The id of the heading the switch belongs to: the group takes its name from it. */
  readonly labelledBy = input.required<string>();

  protected readonly faChartSimple = faChartSimple;
  protected readonly faList = faList;

  /** A press on the view already in use tells the host nothing: a model that is set to the value it holds stays silent. */
  protected show(table: boolean): void {
    this.table.set(table);
  }
}
